
import React, { useState, useEffect } from 'react';
import { SQL_SETUP_SCRIPT, SQL_RESET_SCRIPT, createAdminAccount, checkConnection, fixAdminRole } from '../../services/supabaseBackend';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { useAppContext } from '../../hooks/useAppContext';

interface SetupAssistantProps {
    embedded?: boolean;
}

const SetupAssistant: React.FC<SetupAssistantProps> = ({ embedded = false }) => {
  const { setupRequired, closeSetup } = useAppContext();
  const isDevBootstrapEnabled = import.meta.env.DEV;
  const configuredUrl = import.meta.env.VITE_SUPABASE_URL || '';
  const hasAnonKey = Boolean(import.meta.env.VITE_SUPABASE_ANON_KEY);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<'setup' | 'templates' | 'reset'>('setup');
  
  // Connection Status
  const [connStatus, setConnStatus] = useState<{success?: boolean, message?: string}>({});
  const [isChecking, setIsChecking] = useState(false);

  // Admin Creation State
  const [adminEmail, setAdminEmail] = useState('admin@agm.com');
  const [adminPassword, setAdminPassword] = useState('password123');
  const [adminName, setAdminName] = useState('System Admin');
  const [isCreatingAdmin, setIsCreatingAdmin] = useState(false);
  const [adminCreationMsg, setAdminCreationMsg] = useState<{type: 'success' | 'error', text: string} | null>(null);

  // Manual Schema View
  const [showSchema, setShowSchema] = useState(false);

  useEffect(() => {
    // Auto-check connection on mount
    handleTestConnection();
  }, []);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleTestConnection = async () => {
      setIsChecking(true);
      const result = await checkConnection();
      setIsChecking(false);
      setConnStatus({ success: result.success, message: result.message });
  };

  const handleCreateAdmin = async (e: React.FormEvent) => {
      e.preventDefault();
      setIsCreatingAdmin(true);
      setAdminCreationMsg(null);
      try {
        await createAdminAccount(adminEmail, adminPassword, adminName);
        setAdminCreationMsg({ type: 'success', text: 'Admin account created (or promoted)! You can now login.' });
      } catch (err: any) {
          console.error(err);
          let msg = err.message || "Failed to create admin";
          if (msg.includes("relation") && msg.includes("does not exist")) {
              msg = "Tables missing! Please run the SQL Script (Step 1) first.";
          }
          setAdminCreationMsg({ type: 'error', text: msg });
      } finally {
          setIsCreatingAdmin(false);
      }
  };

  const handleForcePromote = async () => {
    setIsCreatingAdmin(true);
    setAdminCreationMsg(null);
    try {
        const result = await fixAdminRole(adminEmail);
        setAdminCreationMsg({ type: 'success', text: `Success: ${result}` });
    } catch (err: any) {
        setAdminCreationMsg({ type: 'error', text: `Failed: ${err.message}` });
    } finally {
        setIsCreatingAdmin(false);
    }
  };

  const handleReload = () => {
      window.location.reload();
  };

  const renderConfigSection = () => (
      <div className="bg-gray-100 p-4 rounded-md border border-gray-200 mb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-4">
            <div>
              <h3 className="text-md font-bold text-gray-800">Connection Settings</h3>
              <p className="mt-1 text-sm text-gray-600">
                Supabase credentials are read from environment variables. Browser-stored overrides are disabled for better security.
              </p>
            </div>
            <Button size="sm" variant="secondary" onClick={handleTestConnection} disabled={isChecking}>
                {isChecking ? 'Checking...' : 'Test Connection'}
            </Button>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-md border border-gray-200 bg-white p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">VITE_SUPABASE_URL</p>
              <p className="mt-1 break-all text-sm text-gray-800">{configuredUrl || 'Not configured'}</p>
            </div>
            <div className="rounded-md border border-gray-200 bg-white p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">VITE_SUPABASE_ANON_KEY</p>
              <p className="mt-1 text-sm text-gray-800">{hasAnonKey ? 'Configured' : 'Not configured'}</p>
            </div>
          </div>

          <p className="mt-3 text-xs text-gray-500">
            Set these in your `.env` or deployment environment and restart the app after changes.
          </p>
          
          {connStatus.message && (
              <div className={`mt-3 p-2 rounded text-sm font-medium ${connStatus.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {connStatus.success ? '✓' : '✗'} {connStatus.message}
              </div>
          )}
      </div>
  );

  const containerClasses = embedded 
    ? "w-full space-y-8" 
    : "min-h-screen bg-gray-50 flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8";

  const cardClasses = embedded
    ? "bg-white shadow-sm rounded-lg border border-gray-200"
    : "max-w-4xl w-full bg-white shadow-xl";

  const manualSql = `
-- 1. Create the user first in Supabase Auth (Dashboard -> Authentication -> Add User)
-- 2. Then run this to make them an Admin:
UPDATE public.profiles
SET role = 'ADMIN'
WHERE email = '${adminEmail}';
  `.trim();

  const templates = [
      {
          title: "🔥 FIX: Product Deletion Error",
          code: `-- Run this to allow deleting products that have existing orders/reviews
-- This will set the link to NULL but keep the order/review records intact for accounting

-- 1. Fix order_items link
ALTER TABLE order_items
DROP CONSTRAINT IF EXISTS order_items_product_id_fkey,
ADD CONSTRAINT order_items_product_id_fkey
FOREIGN KEY (product_id)
REFERENCES products(id)
ON DELETE SET NULL;

-- 2. Fix testimonials link
ALTER TABLE testimonials
DROP CONSTRAINT IF EXISTS testimonials_product_id_fkey,
ADD CONSTRAINT testimonials_product_id_fkey
FOREIGN KEY (product_id)
REFERENCES products(id)
ON DELETE SET NULL;`
      },
      {
          title: "🛡️ FIX: Security Linter Warnings",
          code: `-- This script fixes "Function Search Path Mutable" warnings
-- by explicitly setting the search_path for sensitive functions.

ALTER FUNCTION public.is_db_setup() SET search_path = public;
ALTER FUNCTION public.promote_to_admin(text) SET search_path = public;
ALTER FUNCTION public.handle_new_user() SET search_path = public;
ALTER FUNCTION public.update_profile_admin(uuid, jsonb) SET search_path = public;

-- To fix "auth_leaked_password_protection":
-- This is a Supabase Dashboard setting.
-- 1. Go to Authentication > Providers > Email
-- 2. Enable "Enable leaked password protection"`
      },
      {
          title: "Add Product Manually",
          code: `INSERT INTO products (name, description, stock_quantity, image_url)
VALUES ('New Office Chair', 'Ergonomic mesh chair', 50, 'https://example.com/chair.jpg');`
      },
      {
          title: "Update Product Stock",
          code: `UPDATE products SET stock_quantity = 100 WHERE id = 1;`
      },
      {
          title: "Delete User (Full Cleanup)",
          code: `-- Delete from profiles first (Auth user remains but is inactive in app)
DELETE FROM profiles WHERE email = 'user@example.com';

-- To delete from auth.users, you must do it via the Supabase Dashboard UI (Authentication > Users)`
      },
      {
          title: "Clear All Orders",
          code: `DELETE FROM order_items;
DELETE FROM invoices;
DELETE FROM orders;`
      }
  ];

  return (
    <div className={containerClasses}>
      {!embedded && (
        <div className="text-center mb-8">
            <h1 className="text-4xl font-extrabold text-indigo-600">AGM Backend Assistant</h1>
            <p className="mt-2 text-xl text-gray-600">Manage your Supabase Database</p>
        </div>
      )}

      <Card className={cardClasses}>
            <div className="p-6">
                
                {renderConfigSection()}

                {/* TABS */}
                <div className="flex border-b border-gray-200 mb-6 overflow-x-auto">
                    <button
                        className={`py-2 px-4 text-sm font-medium whitespace-nowrap ${activeTab === 'setup' ? 'border-b-2 border-indigo-500 text-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
                        onClick={() => setActiveTab('setup')}
                    >
                        Initial Setup
                    </button>
                    <button
                        className={`py-2 px-4 text-sm font-medium whitespace-nowrap ${activeTab === 'templates' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                        onClick={() => setActiveTab('templates')}
                    >
                        SQL Templates (Cheat Sheet)
                    </button>
                    <button
                        className={`py-2 px-4 text-sm font-medium whitespace-nowrap ${activeTab === 'reset' ? 'border-b-2 border-red-500 text-red-600' : 'text-gray-500 hover:text-gray-700'}`}
                        onClick={() => setActiveTab('reset')}
                    >
                        Reset / Wipe Database
                    </button>
                </div>

                {activeTab === 'setup' && (
                    <div className="space-y-6">
                         {setupRequired && (
                            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm text-yellow-700">
                                            <strong>Setup Required</strong><br/>
                                            The application is connected, but the database structure is incomplete.
                                        </p>
                                    </div>
                                </div>
                            </div>
                         )}

                        <div>
                            <div className="flex justify-between items-end mb-2">
                                <div>
                                    <h3 className="text-lg font-medium text-gray-900">Step 1: Create Database Tables</h3>
                                    <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full font-bold">Updated & Optimized</span>
                                </div>
                                <button onClick={() => setShowSchema(!showSchema)} className="text-sm text-indigo-600 hover:text-indigo-800 underline">
                                    {showSchema ? 'Hide Manual Schema' : 'Manual Creation Guide'}
                                </button>
                            </div>
                            <div className="bg-blue-50 p-3 rounded-md mb-3 text-sm text-blue-800">
                                <strong>Why do I need to run this manually?</strong>
                                <br/>
                                For security, web browsers cannot create or delete database tables directly. You must grant permission by running this script in your secure Supabase Dashboard.
                            </div>
                            <p className="text-sm text-gray-500 mb-2">
                                Copy this script. It includes all tables, security policies, and <strong>sample data</strong> to get you started.
                            </p>
                            
                            {!showSchema ? (
                                <div className="relative">
                                    <pre className="bg-gray-800 text-green-400 p-4 rounded-md overflow-x-auto text-xs h-64">
                                        {SQL_SETUP_SCRIPT}
                                    </pre>
                                    <button 
                                        onClick={() => handleCopy(SQL_SETUP_SCRIPT)}
                                        className="absolute top-2 right-2 inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
                                    >
                                        {copied ? 'Copied!' : 'Copy SQL'}
                                    </button>
                                </div>
                            ) : (
                                <div className="bg-gray-50 border border-gray-200 rounded-md p-4 text-sm space-y-4">
                                    <p className="font-semibold text-gray-700">If you cannot run SQL, create these tables manually:</p>
                                    <ul className="list-disc pl-5 space-y-2 text-gray-600">
                                        <li><strong>profiles</strong>: id (uuid, pk), email (text), role (text), name, phone, etc.</li>
                                        <li><strong>categories</strong>, <strong>products</strong>, <strong>orders</strong>, <strong>order_items</strong>, <strong>invoices</strong></li>
                                        <li><strong>order_approvals</strong>, <strong>order_events</strong>, <strong>shipments</strong></li>
                                        <li><strong>inventory_items</strong>, <strong>inventory_reorder_rules</strong>, <strong>payment_transactions</strong></li>
                                        <li><strong>testimonials</strong>, <strong>notifications</strong>, <strong>inquiries</strong>, <strong>site_content</strong></li>
                                    </ul>
                                    <p className="text-xs text-gray-500 italic mt-2">Note: The SQL script also installs secure RPC functions for admin features. Manual creation might miss these.</p>
                                </div>
                            )}
                        </div>

                        <div>
                            <h3 className="text-lg font-medium text-gray-900">Step 2: Run in Supabase</h3>
                            <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1 mt-2">
                                <li>Go to your <strong>Supabase Dashboard</strong>.</li>
                                <li>Navigate to the <strong>SQL Editor</strong> (Icon looks like {'>'}_).</li>
                                <li>Paste the code and click <strong>Run</strong>.</li>
                            </ol>
                            <div className="mt-4">
                                 <Button size="sm" onClick={handleTestConnection}>
                                    I ran the script, Re-check Connection
                                </Button>
                            </div>
                        </div>

                        <div className="bg-indigo-50 p-4 rounded-md border border-indigo-100">
                            <h3 className="text-lg font-medium text-gray-900 mb-2">Step 3: Create Admin User</h3>
                            <p className="text-sm text-gray-500 mb-4">
                              {isDevBootstrapEnabled
                                ? 'After running the script, you can bootstrap an admin account here in local development.'
                                : 'Admin bootstrap is disabled in production builds. Use the Supabase dashboard or a controlled server-side admin workflow.'}
                            </p>

                            {isDevBootstrapEnabled ? (
                              <>
                                <form onSubmit={handleCreateAdmin} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end mb-4">
                                    <Input
                                        label="Admin Email"
                                        value={adminEmail}
                                        onChange={(e) => setAdminEmail(e.target.value)}
                                        required
                                    />
                                    <Input
                                        label="Password"
                                        type="password"
                                        value={adminPassword}
                                        onChange={(e) => setAdminPassword(e.target.value)}
                                        required
                                    />
                                    <div className="flex space-x-2">
                                        <Button type="submit" disabled={isCreatingAdmin}>
                                            {isCreatingAdmin ? 'Processing...' : 'Create Admin'}
                                        </Button>
                                    </div>
                                </form>

                                 <div className="mt-2 flex items-center justify-between">
                                    <p className="text-xs text-indigo-700">
                                        Already have an account? Use "Fix/Force Admin Role" to upgrade it.
                                    </p>
                                    <Button size="sm" variant="secondary" onClick={handleForcePromote} disabled={isCreatingAdmin}>
                                        Fix/Force Admin Role
                                    </Button>
                                 </div>

                                {adminCreationMsg && (
                                    <p className={`mt-2 text-sm font-medium ${adminCreationMsg.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                                        {adminCreationMsg.text}
                                    </p>
                                )}
                              </>
                            ) : null}
                            
                            <div className="border-t border-indigo-200 mt-4 pt-4">
                                <h4 className="text-sm font-bold text-indigo-900 mb-2">Manual SQL Query Generator</h4>
                                <p className="text-xs text-gray-600 mb-2">If you prefer to run raw SQL in Supabase Editor to promote an existing user:</p>
                                <div className="relative">
                                    <pre className="bg-gray-800 text-yellow-300 p-3 rounded text-xs overflow-x-auto font-mono">
                                        {manualSql}
                                    </pre>
                                    <button 
                                        onClick={() => handleCopy(manualSql)}
                                        className="absolute top-2 right-2 px-2 py-1 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700"
                                    >
                                        Copy SQL
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'templates' && (
                    <div className="space-y-6">
                        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
                            <p className="text-sm text-blue-700">
                                Use these SQL templates to manually manage data in your Supabase SQL Editor if the UI buttons are not sufficient.
                            </p>
                        </div>
                        <div className="grid gap-6">
                            {templates.map((t, idx) => (
                                <div key={idx} className="border rounded-md p-4 bg-gray-50">
                                    <h4 className="font-bold text-gray-800 mb-2">{t.title}</h4>
                                    <div className="relative">
                                        <pre className="bg-gray-800 text-blue-300 p-3 rounded text-xs overflow-x-auto font-mono">
                                            {t.code}
                                        </pre>
                                        <button 
                                            onClick={() => handleCopy(t.code)}
                                            className="absolute top-2 right-2 px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                                        >
                                            Copy
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {activeTab === 'reset' && (
                    <div className="space-y-6">
                        <div className="bg-red-50 border-l-4 border-red-500 p-4">
                             <h3 className="text-lg font-bold text-red-700 mb-2">Danger Zone</h3>
                             <p className="text-sm text-red-600 mb-4">
                                 The script below will <strong>DROP ALL TABLES</strong> in your Supabase project. 
                                 All data (orders, products, customer profiles) will be lost.
                             </p>
                             <div className="relative">
                                <pre className="bg-gray-800 text-red-300 p-4 rounded-md overflow-x-auto text-xs h-64">
                                    {SQL_RESET_SCRIPT}
                                </pre>
                                <button 
                                    onClick={() => handleCopy(SQL_RESET_SCRIPT)}
                                    className="absolute top-2 right-2 inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none"
                                >
                                    {copied ? 'Copied!' : 'Copy SQL'}
                                </button>
                            </div>
                            <p className="text-xs text-gray-500 mt-2">
                                After running this in Supabase, reload this page. You will be prompted to run the "Initial Setup" again.
                            </p>
                        </div>
                    </div>
                )}
            </div>

            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between items-center rounded-b-lg">
                <Button variant="secondary" onClick={handleReload}>Reload App</Button>
                {embedded ? (
                    <span className="text-xs text-gray-400">Settings Mode</span>
                ) : (
                    <Button onClick={closeSetup}>Back to App</Button>
                )}
            </div>
      </Card>
    </div>
  );
};

export default SetupAssistant;
